import {
  cellChips,
  cellLines,
  shortLabel,
  type SelectedGridCell,
} from "@/features/data-curation/cellSelection";
import type { DatasetMonitorCadence } from "@/features/data-curation/types";

export const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

/** A Monitor request names at most this many rows and this many other cells. */
export const MAX_MONITOR_ITEMS = 100;

export const MAX_MONITOR_DIRECTIONS = 4000;

/** What a monitor looks after, from the selected cells of the grid. */
export interface MonitorScope {
  /** Every cell of the dataset is selected. */
  all: boolean;
  /** Columns whose cells are all selected, in every row that the grid shows. */
  columns: { columnId: string; columnLabel: string }[];
  /** Rows whose cells are all selected, in every column. */
  rows: { rowId: string; rowNumber: number | null | undefined; rowLabel: string }[];
  /** The selected cells outside those columns and rows. */
  cells: SelectedGridCell[];
}

export interface MonitorGrid {
  rowCount: number;
  columnCount: number;
  /** False while a status filter hides rows: a column is then never whole. */
  allRows: boolean;
  /** False while a column filter hides columns: a row is then never whole. */
  allColumns: boolean;
}

export const WHOLE_DATASET: MonitorScope = { all: true, columns: [], rows: [], cells: [] };

export const EMPTY_SCOPE: MonitorScope = { all: false, columns: [], rows: [], cells: [] };

const groupBy = (cells: SelectedGridCell[], key: (cell: SelectedGridCell) => string) => {
  const groups = new Map<string, SelectedGridCell[]>();
  for (const cell of cells) {
    const group = groups.get(key(cell));
    if (group) group.push(cell);
    else groups.set(key(cell), [cell]);
  }
  return [...groups.values()];
};

/**
 * The whole columns, whole rows, and other cells of a selection. Other pages of
 * rows do not matter: a column that is whole on this page is a whole column.
 * With one row on the page, a selected cell is a whole column, not a row.
 */
export const monitorScope = (
  cells: SelectedGridCell[],
  grid: MonitorGrid,
): MonitorScope => {
  const { rowCount, columnCount, allRows, allColumns } = grid;
  if (allRows && allColumns && cells.length > 0 && cells.length === rowCount * columnCount) {
    return WHOLE_DATASET;
  }
  const columns = allRows
    ? groupBy(cells, (cell) => cell.columnId)
        .filter((group) => group.length === rowCount)
        .map(([cell]) => ({ columnId: cell.columnId, columnLabel: cell.columnLabel }))
    : [];
  const rows =
    allColumns && rowCount > 1
      ? groupBy(cells, (cell) => cell.rowId)
          .filter((group) => group.length === columnCount)
          .map(([cell]) => ({
            rowId: cell.rowId,
            rowNumber: cell.rowNumber,
            rowLabel: cell.rowLabel,
          }))
      : [];
  const wholeColumns = new Set(columns.map((column) => column.columnId));
  const wholeRows = new Set(rows.map((row) => row.rowId));
  return {
    all: false,
    columns,
    rows,
    cells: cells.filter(
      (cell) => !wholeColumns.has(cell.columnId) && !wholeRows.has(cell.rowId),
    ),
  };
};

export const monitorScopeTooLarge = (scope: MonitorScope) =>
  scope.rows.length > MAX_MONITOR_ITEMS || scope.cells.length > MAX_MONITOR_ITEMS;

export const monitorScopeIsEmpty = (scope: MonitorScope) =>
  !scope.all && scope.columns.length === 0 && scope.rows.length === 0 && scope.cells.length === 0;

/** One chip of a scope: a column, a row, or a run of cells in one column. */
export type MonitorScopeItem = { key: string; label: string } & (
  | { kind: "column"; columnId: string }
  | { kind: "row"; rowId: string }
  | { kind: "cells"; cells: SelectedGridCell[] }
);

export const monitorScopeItems = (scope: MonitorScope): MonitorScopeItem[] => [
  ...scope.columns.map((column) => ({
    kind: "column" as const,
    key: `column-${column.columnId}`,
    label: `Column “${shortLabel(column.columnLabel)}”`,
    columnId: column.columnId,
  })),
  ...scope.rows.map((row) => ({
    kind: "row" as const,
    key: `row-${row.rowId}`,
    label: row.rowNumber != null ? `Row ${row.rowNumber}` : `Row “${shortLabel(row.rowLabel)}”`,
    rowId: row.rowId,
  })),
  ...cellChips(scope.cells).map((chip) => ({
    kind: "cells" as const,
    key: chip.key,
    label: chip.label,
    cells: chip.cells,
  })),
];

export const withoutScopeItem = (scope: MonitorScope, item: MonitorScopeItem): MonitorScope => {
  if (item.kind === "column") {
    return {
      ...scope,
      columns: scope.columns.filter((column) => column.columnId !== item.columnId),
    };
  }
  if (item.kind === "row") {
    return { ...scope, rows: scope.rows.filter((row) => row.rowId !== item.rowId) };
  }
  const removed = new Set(item.cells);
  return { ...scope, cells: scope.cells.filter((cell) => !removed.has(cell)) };
};

/** Adds a whole column. Its cells that were chosen one by one are then part of it. */
export const withScopeColumn = (
  scope: MonitorScope,
  column: { columnId: string; columnLabel: string },
): MonitorScope =>
  scope.columns.some((item) => item.columnId === column.columnId)
    ? scope
    : {
        ...scope,
        all: false,
        columns: [...scope.columns, column],
        cells: scope.cells.filter((cell) => cell.columnId !== column.columnId),
      };

/** A short name of the scope for the selection bar, or null for loose cells only. */
export const monitorScopeLabel = (scope: MonitorScope): string | null => {
  if (scope.all) return "all cells";
  const parts = [
    scope.columns.length === 1
      ? `column “${shortLabel(scope.columns[0].columnLabel)}”`
      : scope.columns.length > 1
        ? `${scope.columns.length} columns`
        : null,
    scope.rows.length === 1
      ? scope.rows[0].rowNumber != null
        ? `row ${scope.rows[0].rowNumber}`
        : `row “${shortLabel(scope.rows[0].rowLabel)}”`
      : scope.rows.length > 1
        ? `${scope.rows.length} rows`
        : null,
  ].filter((part): part is string => part != null);
  if (parts.length === 0) return null;
  if (scope.cells.length > 0) {
    parts.push(`${scope.cells.length} other ${scope.cells.length === 1 ? "cell" : "cells"}`);
  }
  return parts.join(", ");
};

export interface MonitorSchedule {
  cadence: DatasetMonitorCadence;
  /** Weekly: 0 is Monday. */
  weekday: number;
  /** Monthly: 1 to 28. */
  monthDay: number;
  /** HH:MM, 24-hour. */
  time: string;
  /** IANA name, such as America/New_York. */
  timeZone: string;
}

export const browserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

/** Weekly at 09:00 on today's weekday; monthly runs on today's day, at most the 28th. */
export const defaultMonitorSchedule = (now = new Date()): MonitorSchedule => ({
  cadence: "weekly",
  weekday: (now.getDay() + 6) % 7,
  monthDay: Math.min(now.getDate(), 28),
  time: "09:00",
  timeZone: browserTimeZone(),
});

export const isMonitorTime = (value: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

export const monitorScheduleText = (schedule: MonitorSchedule) => {
  const at = `at ${schedule.time}`;
  if (schedule.cadence === "daily") return `every day ${at}`;
  if (schedule.cadence === "weekly") return `every ${WEEKDAYS[schedule.weekday]} ${at}`;
  return `monthly on day ${schedule.monthDay} ${at}`;
};

const scheduleOptions = (schedule: MonitorSchedule) => {
  const day =
    schedule.cadence === "weekly"
      ? ` --day ${WEEKDAYS[schedule.weekday].toLowerCase()}`
      : schedule.cadence === "monthly"
        ? ` --day ${schedule.monthDay}`
        : "";
  return `--every ${schedule.cadence}${day} --at ${schedule.time} --timezone ${schedule.timeZone}`;
};

const updateLines = (scope: MonitorScope) =>
  scope.all
    ? [
        "- Update values: check all the values of the dataset, and change the ones that are out of date.",
      ]
    : [
        "- Update values: check these values, and change the ones that are out of date:",
        ...[
          ...scope.columns.map(
            (column) =>
              `- Column "${column.columnLabel}" (column_id ${column.columnId}), all rows`,
          ),
          ...scope.rows.map(
            (row) =>
              `- ${row.rowNumber != null ? `Row ${row.rowNumber} ` : "Row "}"${shortLabel(row.rowLabel)}" (row_id ${row.rowId}), all columns`,
          ),
          ...(scope.cells.length > 0 ? cellLines(scope.cells).split("\n") : []),
        ].map((line) => `  ${line}`),
      ];

const scopeOptions = (update: MonitorScope | null) =>
  update && !update.all && (update.columns.length > 0 || update.rows.length > 0)
    ? "Give its columns with --column and its rows with --row."
    : "Leave out --column and --row.";

export interface MonitorRequest {
  datasetId: string;
  /** Each run looks for new sources and adds their rows. */
  addRows: boolean;
  /** The values that each run checks; null when it checks none. */
  update: MonitorScope | null;
  schedule: MonitorSchedule;
  directions: string;
}

/** The chat message of the Monitor dialog: the agent writes the monitor's prompt and creates it. */
export const monitorRequestMessage = ({
  datasetId,
  addRows,
  update,
  schedule,
  directions,
}: MonitorRequest) =>
  [
    "Create a monitor of this dataset.",
    "",
    `How often: ${monitorScheduleText(schedule)}, time zone ${schedule.timeZone}. Use ${scheduleOptions(schedule)}.`,
    "",
    "What each run does:",
    ...(addRows
      ? ["- Add new rows: look for new sources that fit this dataset, and add their rows."]
      : []),
    ...(update ? updateLines(update) : []),
    "",
    "Directions:",
    directions.trim() || "None. Work from the goal and the row definition of the dataset.",
    "",
    `Write the monitor's prompt from this request so that it stands alone, and create the monitor with paperclip extraction monitor create ${datasetId}. ${scopeOptions(update)} Then tell me its name, its schedule, and its next run.`,
  ].join("\n");
